import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
import './utils/enableLogging' // Auto-load logging utilities
import { setupGlobalInterceptors } from './utils/setupGlobalInterceptors'
import { installBfcacheGuard } from './utils/bfcacheGuard'
import { redirectToLogin } from './api/auth-handler'

// Setup global interceptors for handling authentication errors
setupGlobalInterceptors();

// Re-validate the session whenever a page is restored from bfcache (React does
// not remount on restore, so CommonAuthContext's on-mount check is skipped).
// Reuses the same /auth/validate endpoint; the global interceptor above still
// silently refreshes an expired JWT, so only a truly dead session redirects.
installBfcacheGuard(
  async () => {
    const res = await fetch('/api/v1/auth/validate', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return false;
    try {
      return (await res.json()).valid !== false;
    } catch {
      return true; // 200 with an unreadable body — treat as still valid
    }
  },
  redirectToLogin
);

// Global handler for CSS preload errors
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('Unable to preload CSS')) {
    console.warn('CSS preload warning (non-critical):', event.message);
    // Prevent the error from bubbling up to the console
    event.preventDefault();
    // Don't stop the application from working
    return true;
  }
}, true);

// Error boundary for better error handling in development
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      const isLight = document.documentElement.getAttribute('data-theme') === 'light'
        || (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches);
      return (
        <div style={{
          padding: '2rem',
          background: isLight ? '#F9FAFB' : '#0A0E27',
          color: isLight ? 'rgba(0,0,0,0.9)' : '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'monospace'
        }}>
          <h1 style={{ color: '#EF4444', marginBottom: '1rem' }}>
            Application Error
          </h1>
          <p style={{ marginBottom: '1rem', textAlign: 'center' }}>
            Something went wrong in the Athena Assistant Pro application.
          </p>
          <details style={{
            background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
            border: `1px solid ${isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)'}`,
            padding: '1rem',
            borderRadius: '8px',
            maxWidth: '80vw',
            overflow: 'auto'
          }}>
            <summary style={{ cursor: 'pointer', marginBottom: '1rem' }}>
              Error Details
            </summary>
            <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
              {this.state.error && this.state.error.toString()}
              <br />
              {this.state.errorInfo.componentStack}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: isLight ? '#2563EB' : '#00D4FF',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Initialize React application
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);